import { Filter, MultiSelectFilter } from '@library/components/filters';
import type {
  LeagueProjectionAttributes,
  LeaguesList
} from '@library/types/leagues';
import type { DateRange } from '@library/types/dates';
import { DateTime } from 'luxon';
import { PrizepicksDatePicker } from '@library/components/datePicker';
import { PrizepicksDateRange } from '@library/components/datePicker/PrizepicksDateRange';
import { Searchbar } from '@library/components/inputs';
import type { SelectOptions } from '@library/types/utils';
import { classNames } from '@library/utils';
import { convertLeagueProjectionAttributesToSelectOptions } from '@library/component-utils/filters';
import { useState } from 'react';

export interface GlobalFiltersProps {
  allowPastDates?: boolean;
  correlationsFilters?: boolean;
  className?: string;
  dateFormat?: string;
  dateRange?: boolean;
  disabled?: boolean;
  durations: Array<LeagueProjectionAttributes>;
  handleMultiSelectDurations?: (value: Array<string>) => void;
  handleSearch?: (value: string) => void;
  handleSelectDateRange?: (dates: DateRange) => void;
  handleSelectLeague: (id: string) => void;
  leaguesList: LeaguesList;
  searchDisabled?: boolean;
  searchValue?: string;
  searchPlaceholder?: string;
  selectedDurations?: Array<string>;
  selectedEndDate?: DateTime;
  selectedStartDate?: DateTime;
  selectedLeagueId?: string;
  showLeagueSelect?: boolean;
  showSearchInput?: boolean;
}

export const GlobalFilters = ({
  allowPastDates = false,
  correlationsFilters = false,
  className = '',
  dateFormat = undefined,
  dateRange = true,
  disabled = false,
  durations,
  handleMultiSelectDurations = () => {},
  handleSearch = () => {},
  handleSelectDateRange = undefined,
  handleSelectLeague,
  leaguesList,
  searchDisabled = false,
  searchValue = '',
  searchPlaceholder = undefined,
  selectedDurations = undefined,
  selectedStartDate = undefined,
  selectedEndDate = undefined,
  selectedLeagueId = leaguesList[0]?.id,
  showLeagueSelect = true,
  showSearchInput = true
}: GlobalFiltersProps) => {
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);

  const options: SelectOptions = leaguesList.map((league) => {
    const { icon, id, imageUrl, name } = league;

    const leagueIcon = (
      <img
        alt={icon}
        className="order-none mr-2 size-6 flex-none grow-0"
        src={imageUrl}
      />
    );

    return {
      key: id,
      icon: leagueIcon,
      name,
      value: id
    };
  });

  const selectedOption = options.find((option) =>
    option.value === selectedLeagueId ? option : null
  );

  const durationOptions: SelectOptions =
    convertLeagueProjectionAttributesToSelectOptions(durations);

  const displayDate =
    !!handleSelectDateRange && !!selectedStartDate && !!selectedEndDate;

  const showDateRange = displayDate && dateRange;
  const showDatePicker = displayDate && !dateRange;

  return (
    <div
      className={classNames(
        'col-span-11 grid gap-4',
        showDateRange
          ? 'grid-cols-[fit-content(100px)_fit-content(100px)_minmax(18rem,25rem)_minmax(11rem,1fr)]'
          : '',
        showDatePicker
          ? 'grid-cols-[fit-content(100px)_fit-content(100px)_minmax(9rem,12.5rem)_minmax(11rem,1fr)]'
          : '',
        !displayDate
          ? 'grid-cols-[fit-content(100px)_fit-content(100px)_1fr]'
          : '',
        !selectedDurations && !className
          ? 'grid-cols-[fit-content(100px)_minmax(9rem,12.5rem)_minmax(11rem,1fr)]'
          : '',
        className
      )}
      data-testid="GlobalFilters"
    >
      {options.length > 0 && showLeagueSelect ? (
        <Filter
          allOption={false}
          disabled={disabled}
          label="leagues"
          onChange={handleSelectLeague}
          options={options}
          selectedValue={selectedOption?.value ?? options[0].value}
          size="medium"
          testId="league-filter"
        />
      ) : (
        <span className="text-soClean-100 flex items-center whitespace-nowrap pr-4 text-base">
          {selectedOption?.icon ?? options[0]?.icon}
          {selectedOption?.name ?? options[0]?.name}
        </span>
      )}
      {selectedDurations && !correlationsFilters && (
        <MultiSelectFilter
          disabled={disabled}
          label="Durations"
          onChange={handleMultiSelectDurations}
          options={durationOptions}
          selectedValues={selectedDurations}
          size="medium"
          showTags
          testId="durations-filter"
          returnAll
        />
      )}
      {showDateRange && (
        <PrizepicksDateRange
          dateFormat={dateFormat}
          disabled={disabled}
          endDate={selectedEndDate.toJSDate()}
          endOpen={endDatePickerOpen}
          handleChange={handleSelectDateRange}
          minDate={!allowPastDates ? new Date() : undefined}
          startOpen={startDatePickerOpen}
          setEndOpen={setEndDatePickerOpen}
          setStartOpen={setStartDatePickerOpen}
          startDate={
            selectedStartDate
              .setZone('system', { keepLocalTime: true })
              .toJSDate() ?? new Date()
          }
        />
      )}
      {showDatePicker && (
        <PrizepicksDatePicker
          disabled={disabled}
          handleChange={(date) =>
            handleSelectDateRange({
              fromDate: DateTime.fromJSDate(date),
              toDate: DateTime.fromJSDate(date)
            })
          }
          minDate={!allowPastDates ? new Date() : undefined}
          open={startDatePickerOpen}
          setOpen={setStartDatePickerOpen}
          value={
            selectedStartDate
              .setZone('system', { keepLocalTime: true })
              .toJSDate() ?? new Date()
          }
        />
      )}
      {showSearchInput && !correlationsFilters && (
        <Searchbar
          searchValue={searchValue}
          handleSearch={handleSearch}
          disabled={searchDisabled}
          placeholder={searchPlaceholder}
        />
      )}
    </div>
  );
};
